import { Icon } from '@advanza/ui'
import clsx from 'clsx'
import { DISCOUNT_STATUS } from 'hooks/providerHooks'
import {
    ProvidersPresenceDiscount,
    STATUS_ON_HOLD,
    STATUS_PENDING,
    TYPE_AMOUNT,
    TYPE_PERCENTAGE,
} from 'interfaces/PresenceSubscription'

interface PresenceDiscountListProps {
    discounts: ProvidersPresenceDiscount[]
    onDeleteDiscount: (discountId: number) => void
    getEffectiveBillingMonth: (date: string | null) => string | null
    hideDelete?: boolean
    hideOptionalText?: boolean
}

const PresenceDiscountList = ({
    discounts,
    onDeleteDiscount,
    getEffectiveBillingMonth,
    hideDelete = false,
    hideOptionalText = false,
}: PresenceDiscountListProps) => {
    if (!discounts || discounts.length === 0) {
        return null
    }

    return (
        <div>
            <div className="font-bold mb-1 text-xl">
                Discount{hideOptionalText ? '' : ' (optional)'}
            </div>
            <div>
                {discounts
                    .sort((a, b) => {
                        // Sort by start_date, with null values at the end
                        if (!a.start_date && !b.start_date) return 0
                        if (!a.start_date) return 1
                        if (!b.start_date) return -1
                        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
                    })
                    .map((discount: ProvidersPresenceDiscount) => {
                        // Check if this is a pending or on_hold discount (for special display)
                        const isPendingOrOnHoldDiscount =
                            discount.status === STATUS_PENDING || discount.status === STATUS_ON_HOLD

                        return (
                            <div
                                key={discount.service_provider_presence_discount_id}
                                className="flex items-center justify-between py-1">
                                <div className="text-md flex-1">
                                    {isPendingOrOnHoldDiscount ? (
                                        // Pending rollover discount display (special format)
                                        <div>
                                            <span className="font-bold">
                                                {discount.months_count || 1} month
                                                {(discount.months_count || 1) === 1 ? '' : 's'}
                                                {discount.type === TYPE_PERCENTAGE &&
                                                discount.amount >= 100
                                                    ? ' free'
                                                    : ''}
                                            </span>
                                            <span className="text-neutral-600 ml-2">
                                                (
                                                {discount.type === TYPE_PERCENTAGE
                                                    ? `${discount.amount}%`
                                                    : `€${discount.amount}`}
                                                )
                                            </span>
                                            {' - '}
                                            <span
                                                className={clsx(
                                                    'font-bold',
                                                    discount.status === STATUS_PENDING ||
                                                        discount.status === STATUS_ON_HOLD
                                                        ? 'text-tertiary-500'
                                                        : discount.status === DISCOUNT_STATUS.ACTIVE
                                                          ? 'text-success'
                                                          : 'text-error',
                                                )}>
                                                {discount.status === STATUS_ON_HOLD
                                                    ? 'On Hold'
                                                    : discount.status.charAt(0).toUpperCase() +
                                                      discount.status.slice(1)}
                                            </span>
                                            {discount.start_date && discount.end_date && (
                                                <span className="text-neutral-500 ml-2">
                                                    -{' '}
                                                    {(() => {
                                                        const startMonth =
                                                            getEffectiveBillingMonth(
                                                                discount.start_date,
                                                            ) || ''
                                                        const endMonth =
                                                            getEffectiveBillingMonth(
                                                                discount.end_date,
                                                            ) || ''

                                                        // If effective billing months are the same, just show one month
                                                        if (startMonth === endMonth) {
                                                            return endMonth
                                                        }

                                                        return `${startMonth} until ${endMonth}`
                                                    })()}
                                                </span>
                                            )}
                                            {discount.expiry_date && !discount.end_date && (
                                                <span className="text-neutral-500 ml-2">
                                                    - expires{' '}
                                                    {new Date(
                                                        discount.expiry_date,
                                                    ).toLocaleDateString('en-GB')}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        // Active discount display (unified for all sources)
                                        <div>
                                            <span className="font-bold">
                                                {discount.type === TYPE_AMOUNT ? '€' : ''}
                                                {Number(discount.amount).toFixed(
                                                    discount.type === TYPE_AMOUNT ? 2 : 0,
                                                )}
                                                {discount.type === TYPE_PERCENTAGE ? '%' : ''}
                                            </span>
                                            {' - '}
                                            {discount.status === DISCOUNT_STATUS.ACTIVE ? (
                                                <span className="text-success font-bold">
                                                    {discount.status.charAt(0).toUpperCase() +
                                                        discount.status.slice(1)}
                                                </span>
                                            ) : (
                                                <span className="text-error font-bold">
                                                    {discount.status.charAt(0).toUpperCase() +
                                                        discount.status.slice(1)}
                                                </span>
                                            )}
                                            {discount.start_date && discount.end_date && (
                                                <span className="text-neutral-500 ml-2">
                                                    -{' '}
                                                    {(() => {
                                                        const startMonth =
                                                            getEffectiveBillingMonth(
                                                                discount.start_date,
                                                            ) || ''
                                                        const endMonth =
                                                            getEffectiveBillingMonth(
                                                                discount.end_date,
                                                            ) || ''

                                                        // If effective billing months are the same, just show one month
                                                        if (startMonth === endMonth) {
                                                            return endMonth
                                                        }

                                                        // Check if it's a single month discount
                                                        const startDate = new Date(
                                                            discount.start_date,
                                                        )
                                                        const endDate = new Date(discount.end_date)

                                                        // Calculate the difference in months
                                                        const monthDiff =
                                                            (endDate.getFullYear() -
                                                                startDate.getFullYear()) *
                                                                12 +
                                                            (endDate.getMonth() -
                                                                startDate.getMonth())
                                                        const isSingleMonth = monthDiff === 1

                                                        if (isSingleMonth) {
                                                            // For single month discounts, show the billing month (end month)
                                                            return endMonth
                                                        } else {
                                                            // For multi-month discounts, show billing months
                                                            // Start month should be the billing month of the first period
                                                            const firstPeriodEnd = new Date(
                                                                startDate,
                                                            )
                                                            firstPeriodEnd.setMonth(
                                                                firstPeriodEnd.getMonth() + 1,
                                                            )
                                                            const firstBillingMonth =
                                                                firstPeriodEnd.toLocaleDateString(
                                                                    'en-GB',
                                                                    {
                                                                        month: 'long',
                                                                        year: 'numeric',
                                                                    },
                                                                )

                                                            return `${firstBillingMonth} until ${endMonth}`
                                                        }
                                                    })()}
                                                </span>
                                            )}
                                            {discount.start_date && !discount.end_date && (
                                                <span className="text-neutral-500 ml-2">
                                                    - starts{' '}
                                                    {new Date(
                                                        discount.start_date,
                                                    ).toLocaleDateString('en-GB')}
                                                </span>
                                            )}
                                            {!discount.start_date && discount.end_date && (
                                                <span className="text-neutral-600 ml-1">
                                                    {discount.status === DISCOUNT_STATUS.ACTIVE
                                                        ? 'until '
                                                        : 'expired '}
                                                    {new Date(discount.end_date).toLocaleDateString(
                                                        'en-GB',
                                                    )}
                                                </span>
                                            )}
                                            {!discount.start_date && !discount.end_date && (
                                                <span className="text-neutral-600 ml-1">
                                                    {discount.status === DISCOUNT_STATUS.ACTIVE
                                                        ? 'until '
                                                        : 'expired '}
                                                    <Icon
                                                        name="all_inclusive"
                                                        classNameIcon="text-xl"
                                                    />
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!hideDelete && (
                                    <div className="ml-2 flex-shrink-0">
                                        {(discount.status === DISCOUNT_STATUS.ACTIVE &&
                                            !isPendingOrOnHoldDiscount) ||
                                        (isPendingOrOnHoldDiscount &&
                                            (discount.status === STATUS_PENDING ||
                                                discount.status === STATUS_ON_HOLD)) ? (
                                            <Icon
                                                name="delete"
                                                classNameIcon="hover:text-error cursor-pointer"
                                                onClick={() =>
                                                    onDeleteDiscount(
                                                        discount.service_provider_presence_discount_id,
                                                    )
                                                }
                                            />
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}

export default PresenceDiscountList
